import type { RowData } from "@tanstack/react-table";
import { CircleAlertIcon, InboxIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { TableBody, TableCell, TableRow } from "@/components/ui/table";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";
import { useDataTableState } from "./data-table-state-provider";
import type { DataTableDensity, DataTableInstance } from "./use-data-table";
import { getDataTablePinningStyle } from "./use-data-table-column-pinning";

export type DataTableStateProps = {
  isLoading?: boolean;
  isError?: boolean;
  isRefreshing?: boolean;
  onRetry?: () => void;
  loadingLabel?: string;
  errorTitle?: string;
  errorDescription?: string;
  retryLabel?: string;
  loading?: ReactNode;
  /** 首次加载与刷新共用的原生骨架行，不嵌套额外的表格或表头。 */
  loadingRows?: ReactNode;
  empty?: ReactNode;
  error?: ReactNode;
  emptyTitle?: ReactNode;
  emptyDescription?: string;
  emptyMedia?: ReactNode;
  emptyActions?: ReactNode;
};

type DataTableBodyProps<TData extends RowData> = DataTableStateProps & {
  density: DataTableDensity;
  table: DataTableInstance<TData>;
};

function BodyState({ state, hasRows, ...props }: DataTableStateProps & { state: "loading" | "empty" | "error"; hasRows: boolean }) {
  const { t } = useI18n();
  const custom = props[state];
  if (custom === null || custom === false) return null;

  return (
    <div
      className={cn("data-table-state sticky start-0 flex w-full min-w-0 items-center justify-center text-center", !hasRows && "min-h-64")}
      data-state={state}
      role={state === "error" ? "alert" : "status"}
    >
      {state === "loading" && hasRows && custom === undefined ? (
        <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
          <Spinner />
          <span>{props.loadingLabel ?? t("request.loading")}</span>
        </div>
      ) : custom !== undefined ? (
        custom
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant={state === "loading" ? "default" : "icon"}>
              {state === "loading" ? <Spinner /> : state === "error" ? <CircleAlertIcon /> : (props.emptyMedia ?? <InboxIcon />)}
            </EmptyMedia>
            <EmptyTitle>
              {state === "loading"
                ? (props.loadingLabel ?? t("request.loading"))
                : state === "error"
                  ? (props.errorTitle ?? t("request.failed.title"))
                  : (props.emptyTitle ?? t("dataTable.empty"))}
            </EmptyTitle>
            {state === "error" ? <EmptyDescription>{props.errorDescription ?? t("request.failed.description")}</EmptyDescription> : null}
            {state === "empty" && props.emptyDescription ? <EmptyDescription>{props.emptyDescription}</EmptyDescription> : null}
          </EmptyHeader>
          {state === "error" && props.onRetry ? (
            <EmptyContent>
              <Button disabled={props.isRefreshing} size="sm" variant="outline" onClick={props.onRetry}>
                {props.retryLabel ?? t("request.retry")}
              </Button>
            </EmptyContent>
          ) : null}
          {state === "empty" && props.emptyActions ? <EmptyContent>{props.emptyActions}</EmptyContent> : null}
        </Empty>
      )}
    </div>
  );
}

type DataTableStateBodyProps = DataTableStateProps & {
  children?: ReactNode;
  hasRows: boolean;
  columnCount: number;
};

/** 普通表格和分类树表格共用同一个原生 tbody 状态出口。 */
export function DataTableStateBody({ children, hasRows, columnCount, ...overrides }: DataTableStateBodyProps) {
  const { t } = useI18n();
  const state = useDataTableState(overrides);
  const showState = state.isLoading || state.isError || state.isRefreshing || !hasRows;
  if (!showState && children === undefined) return null;
  if ((state.isLoading || state.isRefreshing) && state.loadingRows !== undefined) {
    return (
      <TableBody aria-busy="true" aria-label={state.loadingLabel ?? t("request.loading")} className="data-table-body">
        {state.loadingRows}
      </TableBody>
    );
  }
  return (
    <TableBody aria-busy={state.isLoading || state.isRefreshing} className="data-table-body">
      {showState ? (
        <TableRow className="border-0 hover:bg-transparent">
          <TableCell className="bg-card p-0 whitespace-normal" colSpan={Math.max(1, columnCount)}>
            <BodyState
              {...state}
              hasRows={!state.isLoading && hasRows}
              state={state.isLoading ? "loading" : state.isError ? "error" : state.isRefreshing ? "loading" : "empty"}
            />
          </TableCell>
        </TableRow>
      ) : null}
      {!state.isLoading ? children : null}
    </TableBody>
  );
}

/** HRMS 的表体状态分层；业务列的 cell 原样渲染。 */
export function DataTableBody<TData extends RowData>({ table, density, ...state }: DataTableBodyProps<TData>) {
  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;

  return (
    <DataTableStateBody {...state} columnCount={table.getAllLeafColumns().length} hasRows={hasRows}>
      {rows.map((row) => (
        <TableRow className="group/row border-0 hover:bg-transparent" data-state={row.getIsSelected() ? "selected" : undefined} key={row.id}>
          {[...row.getStartVisibleCells(), ...row.getCenterVisibleCells(), ...row.getEndVisibleCells()].map((cell) => {
            const pinned = cell.column.getIsPinned();
            const meta = cell.column.columnDef.meta;
            return (
              <TableCell
                className={cn(
                  "h-10 border-b border-border/50 bg-card px-2 py-1 text-sm font-normal transition-colors group-hover/row:bg-muted group-data-[state=selected]/row:bg-accent",
                  density === "compact" ? "px-3 whitespace-nowrap" : "wrap-anywhere whitespace-normal",
                  meta?.tone === "muted" && "text-muted-foreground",
                  meta?.alignment === "center" && "text-center",
                  meta?.alignment === "right" && "text-end",
                  meta?.cellClassName,
                  pinned && "sticky z-10",
                )}
                data-pinned={pinned || undefined}
                key={cell.id}
                style={getDataTablePinningStyle(pinned, cell.column.getPinnedIndex())}
              >
                {density === "compact" ? (
                  <div className="min-w-0 truncate">
                    <table.FlexRender cell={cell} />
                  </div>
                ) : (
                  <table.FlexRender cell={cell} />
                )}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </DataTableStateBody>
  );
}
